import { FlattenPortalRequest, RequestWorkflowResponse } from '@/types/sso';
import useLanguage from '@/utils/hooks/useLanguage';
import { fetchPortalAPI, usePortalSWR, useSso } from '@/utils/providers/SSOProvider';
import { Tab } from '@headlessui/react';
import cn from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import Divider from '../common/Divider';
import FormStepper from '../form/components/FormStepper';
import BankDetails from '../modules/Portal/Request/BankDetails';
import Modal, { ModalClose, ModalHeader, ModalProps, ModalTitle } from './Modal';

interface PortalBankDetailsModalProps extends ModalProps {
    selectedBankAccount?: FlattenPortalRequest;
    onClose: () => void;
}

const PortalBankDetailsModal = (props: PortalBankDetailsModalProps) => {
    const { opened, handlers, selectedBankAccount, onClose } = props;

    const lang = useLanguage();
    const { jwt, onFetchJWT } = useSso();

    const [tabActiveIndex, setTabActiveIndex] = useState(0);

    const workflowPayload = useMemo(() => {
        if (selectedBankAccount?.id) return { id: selectedBankAccount.id };
        else if (selectedBankAccount?.requests) {
            const approvedRequest = selectedBankAccount.requests.find(
                (r) => r.requestStatus === 'APPROVED' && r.requestType === 'WHITELIST_BANK_INFO'
            );
            if (approvedRequest) return { id: approvedRequest.id };
        }
    }, [selectedBankAccount]);

    const workflowsApi = usePortalSWR<RequestWorkflowResponse[]>('/api/whitelist/workflows-banks', () =>
        fetchPortalAPI(onFetchJWT, workflowPayload && '/api/whitelist/workflows', jwt, workflowPayload, 'POST')
    );

    const { data: workflowsData } = workflowsApi;

    const steps = useMemo(
        () => [
            { key: 1, label: lang.navigation.overview },
            { key: 2, label: lang.portal.additionalInformation }
        ],
        [lang]
    );

    useEffect(() => {
        if (opened) {
            if (selectedBankAccount?.id) workflowsApi.mutate();
            else workflowsApi.mutate([]);
        } else {
            setTabActiveIndex(0);
        }
    }, [opened]);

    return (
        <Modal {...props} className="h-auto" size="max-w-xl">
            <ModalHeader>
                <ModalTitle>{lang.portal.bankAccountDetails}</ModalTitle>
                <ModalClose handlers={handlers} />
            </ModalHeader>
            <Divider neutral />
            <FormStepper.Main activeStep={tabActiveIndex} onStep={(step) => setTabActiveIndex(step)} isView>
                {steps.map(({ label }, index) => (
                    <FormStepper.Step key={label} step={index} label={label} />
                ))}
            </FormStepper.Main>
            <Tab.Group defaultIndex={0} selectedIndex={tabActiveIndex} onChange={(index) => setTabActiveIndex(index)}>
                <Tab.List className="hidden">
                    {steps.map(({ label }) => (
                        <Tab key={label} className="text-sm">
                            {label}
                        </Tab>
                    ))}
                </Tab.List>
                <Tab.Panels className="pb-4 w-full h-[500px] overflow-y-auto px-2 sm:px-4">
                    <Tab.Panel className={cn('flex-1 flex flex-col basis-0 gap-4')}>
                        <BankDetails.Section1 selectedBankAccount={selectedBankAccount} workflowsData={workflowsData} />
                    </Tab.Panel>
                    <Tab.Panel className={cn('flex-1 flex flex-col basis-0 gap-4')}>
                        <BankDetails.Section2 selectedBankAccount={selectedBankAccount} />
                    </Tab.Panel>
                </Tab.Panels>
            </Tab.Group>
            <Divider neutral />
            <div className="flex space-x-2 justify-end text-brand-text text-sm items-center p-2 sm:p-4">
                <button
                    type="button"
                    className="rounded-md p-2 px-4 bg-brand-neutral-700 hover:bg-brand-neutral-600"
                    onClick={() => {
                        onClose();
                    }}>
                    {lang.common.close}
                </button>
            </div>
        </Modal>
    );
};

export default PortalBankDetailsModal;
